import { useSubscription } from "@react3l/react3l/services/common-service/use-subscription";
import { ColumnProps } from "antd/lib/table";
import classNames from "classnames";
import {
  DORMITORY_DETAIL_ROUTE,
  HOME_CONTROLLER_DETAIL_ROUTE,
  ROOM_DETAIL_ROUTE,
  TICKET_DETAIL_ROUTE,
} from "config/route-consts";
import { formatDateTime } from "helpers/date-time";
import { Device } from "models/Device";
import { Dormitory } from "models/Dormitory";
import { DormitoryMember } from "models/DormitoryMember";
import { EndUser } from "models/EndUser";
import { EndUserRole } from "models/EndUserRole";
import { HomeController } from "models/HomeController";
import { Issue, IssueStatus, IssueType } from "models/Issue";
import { Scene } from "models/Scene";
import { Status } from "models/Status";
import { Ticket } from "models/Ticket";
import React from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import { Link } from "react-router-dom";
import { endUserRepository } from "repositories/end-user-repository";
import { issueRepository } from "repositories/issue-repository";
import { Observable } from "rxjs";
import "./EndUserDetail.scss";

// Ô tên trỏ tới trang chi tiết; thiếu id thì chỉ hiện chữ.
function renderLinkCell(route: string, id: any, name?: string) {
  return (
    <div className="ant-cell-master__container">
      <div className="cell-master__first-row">
        {id && name ? (
          <Link className="text-link" to={`${route}?id=${id}`}>
            {name}
          </Link>
        ) : (
          name || "—"
        )}
      </div>
    </div>
  );
}

export function useEndUserDetailHook(
  model: EndUser,
  updateModel: (data: EndUser) => void,
  saveModel: (eu: EndUser) => Observable<EndUser>
) {
  const [translate] = useTranslation();
  const endUserId = React.useMemo(() => {
    return model.id ? model.id : null;
  }, [model.id]);

  const history = useHistory();
  const [subscription] = useSubscription();

  const [isEditable, setEditable] = React.useState(false);

  const handleEdit = React.useCallback(() => {
    setEditable(true);
  }, []);

  const handleFinish = React.useCallback(() => {
    setEditable(false);
  }, []);

  const handleSaveModel = React.useCallback(() => {
    subscription.add(saveModel);
    saveModel(model).subscribe((item: EndUser) => {
      updateModel(item);
    });
  }, [saveModel, model, subscription, updateModel]);

  const handleGoHomeController = React.useCallback(
    (id: string | number) => (_event: any) => {
      if (id) {
        history.push(`${HOME_CONTROLLER_DETAIL_ROUTE}?id=${id}`);
      }
    },
    [history]
  );

  const handleDeactiveAccount = React.useCallback(() => {
    endUserRepository.lock([endUserId]).subscribe(
      (res) => {
        if (res) {
          updateModel(res[0]);
        }
      },
      (_err) => {}
    );
  }, [endUserId, updateModel]);

  const handleActiveAccount = React.useCallback(() => {
    endUserRepository.unlock([endUserId]).subscribe(
      (res) => {
        updateModel(res[0]);
      },
      (_err) => {}
    );
  }, [endUserId, updateModel]);

  const homeControllerColumns: ColumnProps<
    HomeController
  >[] = React.useMemo(() => {
    return [
      {
        title: <div className="gradient-text text-upper">HC/MAC</div>,
        key: "id",
        dataIndex: "id",
        width: 180,
        render(id: string, hc: HomeController) {
          return (
            <div className="ant-cell-master__container">
              <div
                className={classNames(
                  "cell-master__first-row",
                  "first-row--link",
                  {
                    "first-row--ellipsis": true,
                  },
                  "text-link"
                )}
                onClick={handleGoHomeController(id)}
              >
                {hc.name}
              </div>
              <div className="cell-master__second-row">{hc.macAddress}</div>
            </div>
          );
        },
      },
      {
        title: <div className="gradient-text text-upper">IP của HC</div>,
        key: "ipAddress",
        dataIndex: "ipAddress",
        width: 180,
        render(ipAddress: string) {
          return (
            <div className="ant-cell-master__container">
              <div
                className={classNames("cell-master__first-row", {
                  "first-row--ellipsis": true,
                })}
              >
                {ipAddress}
              </div>
            </div>
          );
        },
      },
      {
        title: <div className="gradient-text text-upper">Nhà</div>,
        key: "dormitory",
        dataIndex: "dormitory",
        width: 180,
        render(dormitory: Dormitory) {
          return renderLinkCell(DORMITORY_DETAIL_ROUTE, dormitory?.id, dormitory?.name);
        },
      },
      {
        title: <div className="gradient-text text-upper">Trạng thái</div>,
        key: "homeControllerActiveStatus",
        dataIndex: "homeControllerActiveStatus",
        width: 200,
        render(hcStat: Status) {
          return (
            <>
              {hcStat && (
                <div
                  className={`home-controller__status home-controller__status--${hcStat.id} mt-1`}
                >
                  {hcStat?.name}
                </div>
              )}
            </>
          );
        },
      },
    ];
  }, [handleGoHomeController]);

  const dormitoryColumns: ColumnProps<Dormitory>[] = React.useMemo(() => {
    return [
      {
        title: <div className="gradient-text text-upper">Tên nhà</div>,
        key: "name",
        dataIndex: "name",
        width: 150,
        render(name: string, dorm: Dormitory) {
          return (
            <div className="ant-cell-master__container end-user-detail__cell-wrap">
              <Link className="cell-master__first-row text-link" to={`${DORMITORY_DETAIL_ROUTE}?id=${dorm.id}`}>
                {name || "—"}
              </Link>
            </div>
          );
        },
      },
      {
        title: <div className="gradient-text text-upper">Địa chỉ</div>,
        key: "address",
        dataIndex: "address",
        width: 250,
        render(address: string) {
          return (
            <div className="ant-cell-master__container end-user-detail__cell-wrap">
              <div className="cell-master__first-row">{address || "—"}</div>
            </div>
          );
        },
      },
      {
        title: <div className="gradient-text text-upper">Vai trò</div>,
        key: "endUserRole",
        dataIndex: "endUserRole",
        width: 130,
        render(endUserRole: EndUserRole) {
          return (
            <div className="ant-cell-master__container">
              <div className="cell-master__first-row">{endUserRole?.name}</div>
            </div>
          );
        },
      },
      {
        title: <div className="gradient-text text-upper">Thành viên nhà</div>,
        key: "members",
        dataIndex: "members",
        width: 130,
        render(members: DormitoryMember[]) {
          if (!members || members.length === 0) {
            return <span className="text-muted-token">—</span>;
          }
          const names = members
            .slice(0, 3)
            .map((m) => m.endUser?.displayName || m.endUser?.phoneNumber || m.endUser?.email || "—");
          return (
            <div className="end-user-detail__cell-wrap">
              {names.join(", ")}
              {members.length > 3 && <span className="text-muted-token"> +{members.length - 3}</span>}
            </div>
          );
        },
      },
    ];
  }, []);

  const deviceColumns: ColumnProps<Device>[] = React.useMemo(() => {
    return [
      {
        title: <div className="gradient-text text-upper">Tên thiết bị</div>,
        key: "0",
        dataIndex: "name",
        width: 150,
        render(name: string, dorm: Dormitory) {
          return (
            <div className="ant-cell-master__container">
              <div
                className={classNames(
                  "cell-master__first-row",
                  "first-row--link",
                  {
                    "first-row--ellipsis": true,
                  }
                )}
              >
                {name}
              </div>
            </div>
          );
        },
      },
      {
        title: <div className="gradient-text text-upper">Loại thiết bị</div>,
        key: "1",
        dataIndex: "typeName",
        width: 250,
        render(typeName: number) {
          return (
            <div className="ant-cell-master__container">
              <div
                className={classNames("cell-master__first-row", {
                  "first-row--ellipsis": true,
                })}
              >
                {typeName}
              </div>
            </div>
          );
        },
      },
      {
        title: <div className="gradient-text text-upper">Nhà</div>,
        key: "2",
        dataIndex: "dormitoryName",
        width: 130,
        render(dormitoryName: string, device: Device) {
          return renderLinkCell(DORMITORY_DETAIL_ROUTE, device.dormitoryId, dormitoryName);
        },
      },
      {
        title: <div className="gradient-text text-upper">Trạng thái</div>,
        key: "3",
        dataIndex: "statusId",
        width: 130,
        render(statusId: number) {
          const statusValue = (statusId) => {
            switch (statusId) {
              case 1:
                return "Trực tuyến";
              default:
                return "Tắt";
            }
          };
          return (
            <div className="ant-cell-master__container">
              <div className="cell-master__first-row">
                {statusValue(statusId)}
              </div>
            </div>
          );
        },
      },
    ];
  }, []);

  const sceneColumns: ColumnProps<Scene>[] = React.useMemo(() => {
    return [
      {
        title: <div className="gradient-text text-upper">Tên cảnh</div>,
        key: "0",
        dataIndex: "name",
        width: 150,
        render(name: string) {
          return (
            <div className="ant-cell-master__container">
              <div
                className={classNames(
                  "cell-master__first-row",
                  "first-row--link",
                  {
                    "first-row--ellipsis": true,
                  }
                )}
              >
                {name}
              </div>
            </div>
          );
        },
      },
      {
        title: <div className="gradient-text text-upper">Yêu thích</div>,
        key: "1",
        dataIndex: "isFavoriteScene",
        width: 250,
        render(isFavoriteScene: number) {
          return (
            <div className="ant-cell-master__container">
              <div
                className={classNames("cell-master__first-row", {
                  "first-row--ellipsis": true,
                })}
              >
                {isFavoriteScene ? "Yêu thích" : "—"}
              </div>
            </div>
          );
        },
      },
      {
        title: <div className="gradient-text text-upper">Phòng</div>,
        key: "2",
        dataIndex: "roomName",
        width: 130,
        render(roomName: string, scene: Scene) {
          return renderLinkCell(ROOM_DETAIL_ROUTE, scene.roomId, roomName);
        },
      },
      {
        title: <div className="gradient-text text-upper">Nhà</div>,
        key: "3",
        dataIndex: "dormitoryName",
        width: 130,
        render(dormitoryName: string, scene: Scene & { dormitoryId?: string }) {
          return renderLinkCell(DORMITORY_DETAIL_ROUTE, scene.dormitoryId, dormitoryName);
        },
      },
    ];
  }, []);

  const issueColumns: ColumnProps<Issue>[] = React.useMemo(() => {
    return [
      {
        title: <div className="gradient-text text-upper">Tiêu đề</div>,
        key: "title",
        dataIndex: "title",
        width: 250,
        render(title: string) {
          return (
            <div className="ant-cell-master__container">
              <div
                className={classNames("cell-master__first-row", {
                  "first-row--ellipsis": true,
                })}
              >
                {title || "—"}
              </div>
            </div>
          );
        },
      },
      {
        title: <div className="gradient-text text-upper">Loại vấn đề</div>,
        key: "issueType",
        dataIndex: "issueType",
        width: 150,
        render(issueType: IssueType) {
          return (
            <div className="ant-cell-master__container">
              <div className="cell-master__first-row">
                {issueType?.name || "—"}
              </div>
            </div>
          );
        },
      },
      {
        title: <div className="gradient-text text-upper">Trạng thái</div>,
        key: "issueStatus",
        dataIndex: "issueStatus",
        width: 150,
        render(issueStatus: IssueStatus) {
          const getStatusClass = (code: string) => {
            switch (code) {
              case "WAITING":
                return "issue-status--waiting";
              case "PROCESSING":
                return "issue-status--processing";
              case "DONE":
                return "issue-status--done";
              default:
                return "";
            }
          };
          return (
            <div className="ant-cell-master__container">
              {issueStatus && (
                <span className={`issue-status ${getStatusClass(issueStatus.code)}`}>
                  {issueStatus.name}
                </span>
              )}
            </div>
          );
        },
      },
      {
        title: <div className="gradient-text text-upper">Ngày tạo</div>,
        key: "createdAt",
        dataIndex: "createdAt",
        width: 180,
        render(createdAt: any) {
          return (
            <div className="ant-cell-master__container">
              <div className="cell-master__first-row">
                {formatDateTime(createdAt)}
              </div>
            </div>
          );
        },
      },
    ];
  }, []);

  return {
    translate,
    isEditable,
    handleEdit,
    handleFinish,
    homeControllerColumns,
    dormitoryColumns,
    deviceColumns,
    sceneColumns,
    issueColumns,
    handleDeactiveAccount,
    handleActiveAccount,
    handleSaveModel,
  };
}

export function useIssueList(endUserId: string | undefined) {
  const [issues, setIssues] = React.useState<Issue[]>([]);
  const [loading, setLoading] = React.useState<boolean>(false);

  React.useEffect(() => {
    if (!endUserId) {
      setIssues([]);
      return;
    }

    setLoading(true);
    const subscription = issueRepository.listByEndUser(endUserId).subscribe(
      (res) => {
        setIssues(res || []);
        setLoading(false);
      },
      () => {
        setIssues([]);
        setLoading(false);
      }
    );
    return () => subscription.unsubscribe();
  }, [endUserId]);

  return { issues, loading };
}

interface OpenTicketListProps {
  tickets: Ticket[];
}

export function OpenTicketList(props: OpenTicketListProps) {
  const { tickets } = props;
  const history = useHistory();

  const handleGoTicket = React.useCallback(
    (id: string | number) => () => {
      if (id) {
        history.push(`${TICKET_DETAIL_ROUTE}?id=${id}`);
      }
    },
    [history]
  );

  return (
    <>
      {tickets.map((item) => (
        <div className="d-flex flex-column w-100 p-3 open-ticket" key={item.id}>
          <h2>{item.title}</h2>
          <div>
            <i className="tio-calendar_month mr-2"></i>
            {formatDateTime(item.createdAt)}
          </div>
          <div className="align-self-end">
            <span onClick={handleGoTicket(item.id)}>Chi tiết</span>
          </div>
        </div>
      ))}
    </>
  );
}
